import { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import BaseModal from '~/components/common/base-modal/BaseModal';
import PrimaryInput from '~/components/common/primary-input/PrimaryInput';
import PrimarySelectInput from '~/components/common/primary-select-input/PrimarySelectInput';
import BaseTextarea from '~/components/common/base-textarea/BaseTextarea';
import DateTimePicker from './DateTimePicker';
import dayjs from 'dayjs';
import jobApi from '~/apis/job';
import userManagementApi from '~/apis/userManagement';
import scheduleEventApi from '~/apis/schedule';
import jobDetailsApi from '~/apis/jobDetails';
import { catchError, notify } from '~/utils/helper';
import { AppointmentType, JobPriorityEnum, RepeatType, RecurrenceEndType, IScheduleDetailResponse } from '~/utils/interface/schedule';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import styles from './AddAppointmentModal.module.scss';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

interface AddAppointmentModalProps {
  open: boolean;
  onClose: () => void;
  mode?: 'add' | 'view' | 'edit';
  scheduleId?: number;
  onDelete?: (id: number) => void;
  onEdit?: (id: number) => void;
  defaultRelatedJobId?: string;
}

interface FormData {
  appointmentTitle: string;
  description: string;
  appointmentType: AppointmentType | '';
  priority: JobPriorityEnum | '';
  relatedJobId: string;
  startDate: Date | null;
  endDate: Date | null;
  assigneeId: string;
  subcontractorId: string;
  allDayChecked: boolean;
  isRecurring: boolean;
  repeatType: RepeatType | '';
  repeatEvery: number;
  recurrenceEndType: RecurrenceEndType;
  recurrenceEndDate: Date | null;
  recurrenceOccurrences: number | null;
}

const initialFormData: FormData = {
  appointmentTitle: '',
  description: '',
  appointmentType: '',
  priority: '',
  relatedJobId: '',
  startDate: null,
  endDate: null,
  assigneeId: '',
  subcontractorId: '',
  allDayChecked: false,
  isRecurring: false,
  repeatType: '',
  repeatEvery: 1,
  recurrenceEndType: 'Never',
  recurrenceEndDate: null,
  recurrenceOccurrences: null,
};

const AddAppointmentModal = ({ open, onClose, mode = 'add', scheduleId, onDelete, onEdit, defaultRelatedJobId }: AddAppointmentModalProps) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = () => {
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!scheduleId) return;

    setIsDeleting(true);
    try {
      await scheduleEventApi.deleteSchedule(scheduleId);
      notify('success', 'Schedule deleted successfully');
      setOpenDeleteModal(false);
      onClose();
      resetForm();
      // Call the onDelete callback if provided
      if (onDelete) {
        onDelete(scheduleId);
      }
    } catch (error: unknown) {
      catchError(error, (message) => {
        notify('error', message || 'Failed to delete schedule');
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteModal(false);
  };

  const handleEdit = () => {
    if (scheduleId && onEdit) {
      onEdit(scheduleId);
    }
  };

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isAddMode = mode === 'add';

  // Options for selects
  const [appointmentTypeOptions, setAppointmentTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const [priorityOptions, setPriorityOptions] = useState<{ label: string; value: string }[]>([]);
  const [jobOptions, setJobOptions] = useState<{ label: string; value: string }[]>([]);
  const [assignedToOptions, setAssignedToOptions] = useState<{ label: string; value: string }[]>([]);
  const [subcontractorOptions, setSubcontractorOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    if (open) {
      loadOptions();
      if ((mode === 'view' || mode === 'edit') && scheduleId) {
        loadScheduleData();
      } else {
        resetForm();
      }
    }
  }, [open, scheduleId, defaultRelatedJobId]);

  const loadScheduleData = async () => {
    if (!scheduleId) return;

    setIsLoading(true);
    try {
      const response = await scheduleEventApi.getScheduleById(scheduleId);
      const data: IScheduleDetailResponse = response.data.data;

      setFormData({
        appointmentTitle: data.appointmentTitle,
        description: data.description || '',
        appointmentType: data.appointmentType,
        priority: data.priority,
        relatedJobId: data.relatedJobId?.toString() || '',
        startDate: dayjs(data.startAt).toDate(),
        endDate: dayjs(data.endAt).toDate(),
        assigneeId: data.assigneeId?.toString() || '',
        subcontractorId: data.subcontractorId?.toString() || '',
        allDayChecked: data.allDayChecked,
        isRecurring: data.isRecurring,
        repeatType: data.repeatType || '',
        repeatEvery: data.repeatEvery || 1,
        recurrenceEndType: data.recurrenceEndType || 'Never',
        recurrenceEndDate: data.recurrenceEndDate ? dayjs(data.recurrenceEndDate).toDate() : null,
        recurrenceOccurrences: data.recurrenceOccurrences,
      });
    } catch (error) {
      catchError(error, (message) => {
        notify('error', message || 'Failed to load schedule data');
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      // Load appointment types
      setAppointmentTypeOptions([
        { label: 'Task', value: 'Task' },
        { label: 'Phone Call', value: 'Phone Call' },
        { label: 'Initial Appointment', value: 'Initial Appointment' },
        { label: 'Adjuster Meeting', value: 'Adjuster Meeting' },
        { label: 'Estimate Follow Up', value: 'Estimate Follow Up' },
        { label: 'Invoice Follow Up', value: 'Invoice Follow Up' },
        { label: 'Meeting', value: 'Meeting' },
        { label: 'Collections Follow Up', value: 'Collections Follow Up' },
        { label: 'Job Notes', value: 'Job Notes' },
      ]);

      // Load priorities
      setPriorityOptions([
        { label: 'Low', value: 'Low' },
        { label: 'Normal', value: 'Normal' },
        { label: 'High', value: 'High' },
        { label: 'Urgent', value: 'Urgent' },
      ]);

      // Load jobs
      try {
        const jobsResponse = await jobApi.getJobs({ page: 1, limit: 100 });
        if (jobsResponse.data?.data?.responses) {
          const jobs = jobsResponse.data.data.responses.map((job: any) => ({
            label: job.title || `Job #${job.id}`,
            value: job.id?.toString() || '',
          }));
          if (
            defaultRelatedJobId &&
            !jobs.some((job: { value: string }) => job.value === defaultRelatedJobId)
          ) {
            jobs.unshift({ label: `Job #${defaultRelatedJobId}`, value: defaultRelatedJobId });
          }
          setJobOptions(jobs);
        }
      } catch (error) {
        console.error('Error loading jobs:', error);
      }

      // Load employees for Assigned To
      try {
        const employeesResponse = await userManagementApi.getEmployees({
          page: 1,
          limit: 100,
          status: 'ACTIVE',
        });
        if (employeesResponse.data?.data?.responses) {
          const employees = employeesResponse.data.data.responses.map((emp: any) => {
            const fullName = `${emp.profile?.firstName || ''} ${emp.profile?.lastName || ''}`.trim() || emp.username;
            return {
              label: fullName,
              value: emp.id?.toString() || '',
            };
          });
          setAssignedToOptions(employees);
        }
      } catch (error) {
        console.error('Error loading employees:', error);
      }

      // Load subcontractors
      try {
        const subcontractorsResponse = await userManagementApi.getEmployees({
          page: 1,
          limit: 100,
          status: 'ACTIVE',
          role: '00004',
        });
        if (subcontractorsResponse.data?.data?.responses) {
          const subcontractors = subcontractorsResponse.data.data.responses.map((sub: any) => {
            const fullName = `${sub.profile?.firstName || ''} ${sub.profile?.lastName || ''}`.trim() || sub.username;
            return {
              label: fullName,
              value: sub.id?.toString() || '',
            };
          });
          setSubcontractorOptions(subcontractors);
        }
      } catch (error) {
        console.error('Error loading subcontractors:', error);
      }
    } catch (error) {
      catchError(error, (message) => {
        console.error('Error loading options:', message);
      });
    }
  };

  const resetForm = () => {
    setFormData({
      ...initialFormData,
      relatedJobId: defaultRelatedJobId || '',
    });
    setErrors({});
  };

  // Auto-generate appointment title when related job is selected
  useEffect(() => {
    const fetchJobDetailsAndGenerateTitle = async () => {
      if (formData.relatedJobId && mode === 'add') {
        try {
          const response = await jobDetailsApi.getOverviewCustomerDetails(formData.relatedJobId);
          const customerData = response.data.data;

          if (customerData?.primaryCustomer) {
            const nameParts = (customerData.primaryCustomer.name || '')
              .trim()
              .split(/\s+/)
              .filter(Boolean);
            const lastName = nameParts[nameParts.length - 1] || '';
            const zipCode = (customerData.primaryCustomer.postalCode || '').trim();

            if (lastName) {
              setFormData((prev) => ({
                ...prev,
                appointmentTitle: zipCode
                  ? `${lastName.toUpperCase()} - ${zipCode}`
                  : lastName.toUpperCase(),
              }));
            }
          }
        } catch (error) {
          console.error('Error fetching job details:', error);
          // Don't show error to user, just log it
        }
      }
    };

    fetchJobDetailsAndGenerateTitle();
  }, [formData.relatedJobId, mode]);

  const handleInputChange = (field: keyof FormData, value: any) => {
    if (field === 'allDayChecked') {
      setFormData((prev) => {
        const newData = { ...prev, [field]: value };

        if (value === true) {
          // When checking "All day", set dates to span the entire day
          const baseDate = prev.startDate || prev.endDate || new Date();

          // Set start date to beginning of day (00:00:00)
          const startOfDay = dayjs(baseDate).startOf('day').toDate();

          // Set end date to end of day (23:59:59)
          const endOfDay = dayjs(baseDate).endOf('day').toDate();

          newData.startDate = startOfDay;
          newData.endDate = endOfDay;
        }
        // When unchecking, keep the dates as they are

        return newData;
      });
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleDateChange = (field: 'startDate' | 'endDate', date: Date | null) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: date };

      // If "All day" is checked, adjust the dates to span the full day
      if (prev.allDayChecked && date) {
        if (field === 'startDate') {
          // When start date changes, set end date to end of that day
          newData.endDate = dayjs(date).endOf('day').toDate();
          // Also ensure start date is at beginning of day
          newData.startDate = dayjs(date).startOf('day').toDate();
        } else if (field === 'endDate') {
          // When end date changes, set start date to start of that day
          newData.startDate = dayjs(date).startOf('day').toDate();
          // Also ensure end date is at end of day
          newData.endDate = dayjs(date).endOf('day').toDate();
        }
      } else if (field === 'startDate' && date && !prev.allDayChecked) {
        // Auto-set end date to 2 hours after start date
        newData.endDate = dayjs(date).add(2, 'hours').toDate();
      }

      return newData;
    });

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.appointmentTitle.trim()) {
      newErrors.appointmentTitle = 'Appointment title is required';
    }
    if (!formData.appointmentType) {
      newErrors.appointmentType = 'Appointment type is required';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date & time is required';
    }
    if (!formData.endDate) {
      newErrors.endDate = 'End date & time is required';
    }
    if (formData.startDate && formData.endDate) {
      if (formData.allDayChecked) {
        if (formData.endDate < formData.startDate) {
          newErrors.endDate = 'End date must be >= start date for all-day events';
        }
      } else {
        if (formData.endDate <= formData.startDate) {
          newErrors.endDate = 'End date must be > start date';
        }
      }
    }
    if (formData.isRecurring) {
      if (!formData.repeatType) {
        newErrors.repeatType = 'Repeat type is required for recurring appointments';
      }
      if (formData.recurrenceEndType === 'On Date' && !formData.recurrenceEndDate) {
        newErrors.recurrenceEndDate = 'End date is required';
      }
      if (formData.recurrenceEndType === 'After' && (!formData.recurrenceOccurrences || formData.recurrenceOccurrences < 1)) {
        newErrors.recurrenceOccurrences = 'Number of occurrences must be at least 1';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        appointmentTitle: formData.appointmentTitle,
        description: formData.description || null,
        relatedJobId: formData.relatedJobId ? parseInt(formData.relatedJobId) : null,
        allDayChecked: formData.allDayChecked,
        startAt: dayjs(formData.startDate).toISOString(),
        endAt: dayjs(formData.endDate).toISOString(),
        appointmentType: formData.appointmentType as AppointmentType,
        priority: formData.priority as JobPriorityEnum,
        assigneeId: formData.assigneeId ? parseInt(formData.assigneeId) : null,
        subcontractorId: formData.subcontractorId ? parseInt(formData.subcontractorId) : null,
        isRecurring: formData.isRecurring,
        ...(formData.isRecurring && {
          repeatType: formData.repeatType as RepeatType,
          repeatEvery: formData.repeatEvery,
          recurrenceEndType: formData.recurrenceEndType,
          ...(formData.recurrenceEndType === 'On Date' && {
            recurrenceEndDate: dayjs(formData.recurrenceEndDate).toISOString(),
          }),
          ...(formData.recurrenceEndType === 'After' && formData.recurrenceOccurrences && {
            recurrenceOccurrences: formData.recurrenceOccurrences,
          }),
        }),
      };

      if (isEditMode && scheduleId) {
        await scheduleEventApi.updateSchedule(scheduleId, payload);
        notify('success', 'Schedule updated successfully');
      } else {
        await scheduleEventApi.createSchedule(payload);
        notify('success', 'Schedule created successfully');
      }

      onClose();
      resetForm();
    } catch (error: unknown) {
      catchError(error, (message) => {
        notify('error', message || `Failed to ${isEditMode ? 'update' : 'create'} schedule`);
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <>
      <BaseModal open={open} onClose={handleClose} maxWidth="1200px">
        <Box className={cx('add-appointment-modal', { 'view-mode': isViewMode })}>
          {/* Header */}
          <Box className={cx('modal-header')}>
            <Box className={cx('header-content')}>
              <Box>
                <Typography className={cx('modal-title')}>
                  {isViewMode ? 'View Appointment' : isEditMode ? 'Edit Appointment' : 'Add Appointment'}
                </Typography>
              </Box>
              {(isViewMode || isEditMode) && (
                <Box className={cx('header-actions')}>
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-transparent border border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-[8px] font-medium transition-colors"
                  >
                    Delete
                  </button>
                  {isViewMode && (
                    <button
                      onClick={handleEdit}
                      className="px-4 py-2 bg-[#2D80FC] hover:bg-[#2563eb] text-white rounded-[8px] font-medium transition-colors"
                    >
                      Edit
                    </button>
                  )}
                  <button
                    onClick={handleClose}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </Box>
              )}
            </Box>
          </Box>

          {/* Content */}
          {isLoading ? (
            <Box className={cx('modal-content')}>
              <Box className="flex justify-center items-center py-8">
                <Typography className="text-white">Loading...</Typography>
              </Box>
            </Box>
          ) : (
            <Box className={cx('modal-content')}>
              <Box className={cx('content-wrapper')}>
                <Box className={cx('form-row')}>
                  <Box className={cx('form-column', 'left-column')}>
                    <PrimaryInput
                      label="Appointment Title"
                      name="appointmentTitle"
                      placeholder="Appointment Title"
                      value={formData.appointmentTitle}
                      onChange={(e) => handleInputChange('appointmentTitle', e.target.value)}
                      errorMessage={errors.appointmentTitle}
                      disabled={isViewMode}
                    />

                    <Box className="flex flex-col flex-1">
                      <Typography className="text-sm font-medium text-[#949699] mb-1.5">Description</Typography>
                      <textarea
                        className="flex-1 w-full bg-[#071427] border border-[#2d3748] rounded-lg px-3 py-2 text-white placeholder-gray-400 resize-none focus:outline-none focus:border-[#63b3ed] min-h-[120px]"
                        placeholder="Description"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        disabled={isViewMode}
                        readOnly={isViewMode}
                      />
                      {errors.description && (
                        <Typography className="text-red-400 text-xs mt-1">{errors.description}</Typography>
                      )}
                    </Box>
                  </Box>

                  <Box className={cx('form-column', 'right-column')}>
                    <Box className={cx('inline-row')}>
                      <PrimarySelectInput
                        label="Appointment Type"
                        name="appointmentType"
                        placeholder="Select"
                        value={formData.appointmentType}
                        onChange={(value) => handleInputChange('appointmentType', value)}
                        options={appointmentTypeOptions}
                        errorMessage={errors.appointmentType}
                        disabled={isViewMode}
                      />

                      <PrimarySelectInput
                        label="Priority"
                        name="priority"
                        placeholder="None"
                        value={formData.priority}
                        onChange={(value) => handleInputChange('priority', value)}
                        options={priorityOptions}
                        errorMessage={errors.priority}
                        disabled={isViewMode}
                      />
                    </Box>

                    <PrimarySelectInput
                      label="Related Job"
                      name="relatedJobId"
                      placeholder="Select"
                      value={formData.relatedJobId}
                      onChange={(value) => handleInputChange('relatedJobId', value)}
                      options={jobOptions}
                      errorMessage={errors.relatedJobId}
                      disabled={isViewMode}
                    />

                    <Box className={cx('inline-row')}>
                      <Box className={cx('date-time-field')}>
                        <Typography className={cx('date-time-label')}>Start Date & Time</Typography>
                        <DateTimePicker
                          placeholder="Select start date & time"
                          value={formData.startDate}
                          onChange={(date) => handleDateChange('startDate', date)}
                          errorMessage={errors.startDate}
                          disabled={isViewMode}
                        />
                      </Box>

                      <Box className={cx('date-time-field')}>
                        <Typography className={cx('date-time-label')}>End Date & Time</Typography>
                        <DateTimePicker
                          placeholder="Select end date & time"
                          value={formData.endDate}
                          onChange={(date) => handleDateChange('endDate', date)}
                          errorMessage={errors.endDate}
                          disabled={isViewMode}
                        />
                      </Box>
                    </Box>

                    <Box className={cx('inline-row')}>
                      <PrimarySelectInput
                        label="Assigned To"
                        name="assigneeId"
                        placeholder="Select"
                        value={formData.assigneeId}
                        onChange={(value) => handleInputChange('assigneeId', value)}
                        options={assignedToOptions}
                        errorMessage={errors.assigneeId}
                        disabled={isViewMode}
                      />

                      <PrimarySelectInput
                        label="Subcontractors"
                        name="subcontractorId"
                        placeholder="None"
                        value={formData.subcontractorId}
                        onChange={(value) => handleInputChange('subcontractorId', value)}
                        options={subcontractorOptions}
                        errorMessage={errors.subcontractorId}
                        disabled={isViewMode}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Recurring Section */}
              <Box className={cx('content-wrapper', 'mt-6')}>
                <Box className="space-y-4">
                  <Box className="">
                    <Box className="flex items-center space-x-2 mb-2">
                      <input
                        type="checkbox"
                        id="allDay"
                        className="w-4 h-4 bg-[#071427] border border-[#2d3748] rounded focus:ring-[#63b3ed] focus:ring-2"
                        checked={formData.allDayChecked}
                        onChange={(e) => handleInputChange('allDayChecked', e.target.checked)}
                        disabled={isViewMode}
                      />
                      <Typography className="text-sm text-[#edeef0]">All day</Typography>
                    </Box>
                    <Box className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="recurring"
                        className="w-4 h-4 bg-[#2d80fc] border border-[#2d80fc] rounded focus:ring-[#63b3ed] focus:ring-2 checked:bg-[#2d80fc]"
                        checked={formData.isRecurring}
                        onChange={(e) => handleInputChange('isRecurring', e.target.checked)}
                        disabled={isViewMode}
                      />
                      <Typography className="text-sm text-[#edeef0]">Recurring appointment</Typography>
                    </Box>
                  </Box>

                  {formData.isRecurring && (
                    <Box className="grid grid-cols-3 gap-4">
                      <PrimarySelectInput
                        label="Repeat"
                        name="repeatType"
                        placeholder="Select"
                        value={formData.repeatType}
                        onChange={(value) => handleInputChange('repeatType', value)}
                        options={[
                          { label: 'Daily', value: 'Daily' },
                          { label: 'Weekly', value: 'Weekly' },
                          { label: 'Monthly', value: 'Monthly' },
                        ]}
                        errorMessage={errors.repeatType}
                        disabled={isViewMode}
                      />
                      <Box>
                        <Typography className="text-sm text-[#949699] mb-2">Every</Typography>
                        <Box className="relative">
                          <PrimaryInput
                            name="repeatEvery"
                            placeholder="1"
                            value={formData.repeatEvery.toString()}
                            onChange={(e) => handleInputChange('repeatEvery', parseInt(e.target.value) || 1)}
                            disabled={isViewMode}
                          />
                          <Typography className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-[#edeef0] pointer-events-none">
                            {formData.repeatType === 'Daily' ? 'Days' : formData.repeatType === 'Weekly' ? 'Weeks' : 'Months'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box>
                        <Typography className="text-sm text-[#949699] mb-2">Ends</Typography>
                        <Box className="space-y-2">
                          <Box className="flex items-center space-x-2 h-10">
                            <input
                              type="radio"
                              name="ends"
                              id="never"
                              className="w-4 h-4 text-[#2d80fc] bg-[#071427] border-[#2d3748] focus:ring-[#63b3ed]"
                              checked={formData.recurrenceEndType === 'Never'}
                              onChange={() => handleInputChange('recurrenceEndType', 'Never')}
                              disabled={isViewMode}
                            />
                            <Typography className="text-sm text-[#edeef0]">Never</Typography>
                          </Box>
                          <Box className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="ends"
                              id="on"
                              className="w-4 h-4 text-[#2d80fc] bg-[#071427] border-[#2d3748] focus:ring-[#63b3ed]"
                              checked={formData.recurrenceEndType === 'On Date'}
                              onChange={() => handleInputChange('recurrenceEndType', 'On Date')}
                              disabled={isViewMode}
                            />
                            <Typography className="text-sm text-[#6b7280]">On</Typography>
                            <DateTimePicker
                              placeholder="Select"
                              value={formData.recurrenceEndDate}
                              onChange={(date) => handleInputChange('recurrenceEndDate', date)}
                              errorMessage={errors.recurrenceEndDate}
                              disabled={isViewMode}
                            />
                          </Box>
                          <Box className="flex items-center space-x-2 h-10">
                            <input
                              type="radio"
                              name="ends"
                              id="after"
                              className="w-4 h-4 text-[#2d80fc] bg-[#071427] border-[#2d3748] focus:ring-[#63b3ed]"
                              checked={formData.recurrenceEndType === 'After'}
                              onChange={() => handleInputChange('recurrenceEndType', 'After')}
                              disabled={isViewMode}
                            />
                            <Typography className="text-sm text-[#6b7280]">After</Typography>
                            <PrimaryInput
                              name="recurrenceOccurrences"
                              placeholder="Enter"
                              value={formData.recurrenceOccurrences?.toString() || ''}
                              onChange={(e) => handleInputChange('recurrenceOccurrences', parseInt(e.target.value) || null)}
                              className="flex-1"
                              errorMessage={errors.recurrenceOccurrences}
                              disabled={isViewMode}
                            />
                            <Typography className="text-sm text-[#edeef0]">occurrences</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Footer */}
          {(isAddMode || isEditMode) && (
            <Box className={cx('modal-footer')}>
              <Box className={cx('modal-actions')}>
                <Box className={cx('cancel-button')} onClick={handleClose}>
                  Cancel
                </Box>
                <Box
                  className={cx('submit-button', { disabled: isSubmitting })}
                  onClick={handleSubmit}
                >
                  {isSubmitting ? 'Saving...' : isEditMode ? 'Update Appointment' : 'Save Appointment'}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </BaseModal>

      <ModalAlertDelete
        open={openDeleteModal}
        onClose={handleCancelDelete}
        description="Are you sure you want to delete this appointment? This action cannot be undone."
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default AddAppointmentModal;

